'use client';

import { PortableText } from '@portabletext/react';
import { PortableTextBlock } from 'sanity';

import { GROQButton } from '@/sanity/lib/definitions';
import { customRichTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import { FadeIn } from '@/components/global/utils/FadeIn';

interface HeroSectionProps {
  title?: PortableTextBlock[];
  cta?: GROQButton;
}

// Hero (Figma node 3019:482) — one richtext field carries both the serif
// headline (H1 blocks) and the body copy (paragraph blocks); `em` renders as the
// blue italic underline used on "with 6 Titles.".
export default function HeroSection({ title, cta }: HeroSectionProps) {
  return (
    <section className="flex flex-col items-center justify-center w-full px-[2rem] pt-[8rem] pb-[8rem] gap-[2.25rem] lg:px-0 lg:pt-[13.875rem] lg:pb-[5rem] lg:gap-[4rem]">
      {title && (
        <FadeIn
          delay={0}
          className="flex flex-col items-center w-[min(129.125rem,100%)] gap-[2.75rem] lg:gap-[3.5rem]">
          <PortableText
            value={title}
            components={customRichTextComponent({
              block: {
                h1: ({ children }) => (
                  <h1 className="mb-0 w-full font-normal text-center text-white font-news text-[4rem] leading-[1.16] tracking-[-0.08rem] lg:text-[7rem] lg:tracking-[-0.14rem]">
                    {children}
                  </h1>
                ),
                normal: ({ children }) => (
                  <p className="mx-auto mb-0 text-center font-geist text-white/[0.72] text-[2rem] leading-[1.48] tracking-[0.04rem] max-w-[46.25rem] lg:text-[2.25rem] lg:tracking-[0.045rem] lg:max-w-[80.875rem]">
                    {children}
                  </p>
                ),
              },
              marks: {
                em: ({ children }) => (
                  <em className="italic underline text-[#A7DEFF] font-news leading-[1.1] tracking-[-0.25rem] decoration-from-font [text-underline-position:from-font]">
                    {children}
                  </em>
                ),
              },
            })}
          />
        </FadeIn>
      )}

      {cta?.cta && (
        <FadeIn delay={0.1} className="w-full lg:w-auto">
          <CTAButton data={cta} />
        </FadeIn>
      )}
    </section>
  );
}
